import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import {
  Check,
  Copy,
  ExternalLink,
  FileText,
  HelpCircle,
  KeyRound,
  Layers,
  Plus,
  Repeat,
  Save,
  Send,
  ShoppingBag,
  Sparkles,
  Star,
  Trash2,
  User,
  X,
  type LucideIcon,
} from "lucide-react"

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useToast } from "@/hooks/use-toast"
import {
  cobrancaDoItem,
  custoNoPeriodo,
  fmtCurrency,
  novaModalidade,
  publicUrl,
  totaisDosItens,
  uid,
} from "@/lib/propostas"
import { cn } from "@/lib/utils"
import type { CobrancaItem, ItemProposta, ModalidadeProposta, Proposta, TipoModalidade } from "@/types/comercial"

import { StatusPill } from "./propostaUi"

// ── Default empty proposta ────────────────────────────────────────────────────

function emptyProposta(): Proposta {
  const now = new Date().toISOString()
  const validadeDefault = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
  return {
    id: uid(),
    titulo: "",
    clienteNome: "",
    clienteEmail: "",
    clienteTelefone: "",
    clienteEmpresa: "",
    status: "rascunho",
    itens: [],
    valorTotal: 0,
    desconto: 0,
    valorFinal: 0,
    validadeAte: validadeDefault,
    escopo: "",
    observacoes: "",
    termos: "Esta proposta é válida pelo período indicado. Os valores podem ser alterados após o vencimento.",
    token: uid(),
    linkPublico: "",
    senha: "",
    modalidades: [],
    historico: [{ id: uid(), tipo: "criada", data: now, descricao: "Proposta criada" }],
    createdAt: now,
    updatedAt: now,
  }
}

type Secao = "cliente" | "itens" | "modalidades" | "conteudo" | "compartilhar"

const SECOES: { id: Secao; label: string; hint: string; icon: LucideIcon }[] = [
  { id: "cliente", label: "Cliente", hint: "Quem recebe e validade", icon: User },
  { id: "itens", label: "Itens", hint: "O que está sendo vendido", icon: Layers },
  { id: "modalidades", label: "Modalidades", hint: "Compra, locação…", icon: ShoppingBag },
  { id: "conteudo", label: "Conteúdo", hint: "Escopo e condições", icon: FileText },
  { id: "compartilhar", label: "Compartilhar", hint: "Link e senha", icon: Send },
]

// ── Small building blocks ─────────────────────────────────────────────────────

function Field({ label, hint, htmlFor, children, className }: { label: string; hint?: string; htmlFor?: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center gap-1.5">
        <Label htmlFor={htmlFor} className="text-[12px] font-medium text-[hsl(225_8%_74%)]">{label}</Label>
        {hint && (
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="h-3.5 w-3.5 cursor-help text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-64 text-xs leading-relaxed">{hint}</TooltipContent>
          </Tooltip>
        )}
      </div>
      {children}
    </div>
  )
}

function SectionHeader({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
        <p className="mt-0.5 text-[13px] text-muted-foreground">{description}</p>
      </div>
      {action}
    </div>
  )
}

function MoneyInput({ value, onChange, id, className }: { value: number; onChange: (v: number) => void; id?: string; className?: string }) {
  return (
    <div className={cn("relative", className)}>
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-muted-foreground">R$</span>
      <Input
        id={id}
        type="number"
        min="0"
        step="0.01"
        inputMode="decimal"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-9 pl-9 text-right tabular-nums"
      />
    </div>
  )
}

function CobrancaToggle({ value, onChange }: { value: CobrancaItem; onChange: (v: CobrancaItem) => void }) {
  return (
    <div className="inline-flex h-9 rounded-md border border-input bg-[hsl(228_8%_9%)] p-0.5 text-[12px]" role="radiogroup" aria-label="Tipo de cobrança">
      {([["unica", "Única"], ["mensal", "Mensal"]] as const).map(([v, label]) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={value === v}
          onClick={() => onChange(v)}
          className={cn(
            "rounded px-2.5 font-medium text-muted-foreground transition-colors",
            value === v && (v === "mensal" ? "bg-[hsl(258_60%_22%)] text-[hsl(258_100%_86%)]" : "bg-[hsl(228_8%_17%)] text-foreground"),
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

const TIPO_META: Record<TipoModalidade, { label: string; icon: LucideIcon; tone: string }> = {
  compra: { label: "Compra", icon: ShoppingBag, tone: "text-[hsl(224_100%_76%)] bg-[hsl(224_60%_16%)]" },
  locacao: { label: "Locação", icon: Repeat, tone: "text-[hsl(258_100%_82%)] bg-[hsl(258_45%_18%)]" },
  personalizada: { label: "Personalizada", icon: Sparkles, tone: "text-[hsl(38_92%_70%)] bg-[hsl(38_50%_14%)]" },
}

function ModalidadeEditor({
  m,
  index,
  onChange,
  onRemove,
  onRecomendar,
}: {
  m: ModalidadeProposta
  index: number
  onChange: (m: ModalidadeProposta) => void
  onRemove: () => void
  onRecomendar: () => void
}) {
  const meta = TIPO_META[m.tipo]
  const set = <K extends keyof ModalidadeProposta>(k: K, v: ModalidadeProposta[K]) => onChange({ ...m, [k]: v })
  const labelUnico = m.tipo === "locacao" ? "Taxa de adesão" : m.tipo === "compra" ? "Valor de compra" : "Valor único"
  const meses = m.fidelidadeMeses || 12

  return (
    <div className={cn("rounded-xl border bg-[hsl(228_9%_8%)] transition-colors", m.recomendada ? "border-[hsl(224_100%_64%/0.55)]" : "border-border")}>
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg", meta.tone)}>
          <meta.icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <Input
            value={m.nome}
            onChange={(e) => set("nome", e.target.value)}
            aria-label={`Nome da opção ${index + 1}`}
            className="h-8 border-transparent bg-transparent px-1.5 text-[14px] font-semibold shadow-none hover:border-input focus-visible:border-input"
          />
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onRecomendar}
              className={cn(
                "inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-medium transition-colors",
                m.recomendada
                  ? "border-[hsl(224_100%_64%/0.5)] bg-[hsl(224_100%_64%/0.12)] text-[hsl(224_100%_80%)]"
                  : "border-border text-muted-foreground hover:text-foreground",
              )}
            >
              <Star className={cn("h-3 w-3", m.recomendada && "fill-current")} />
              Recomendada
            </button>
          </TooltipTrigger>
          <TooltipContent>Destaca esta opção para o cliente na página da proposta</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--destructive))]" onClick={onRemove} aria-label="Remover opção">
              <Trash2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Remover opção</TooltipContent>
        </Tooltip>
      </div>

      <div className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={labelUnico} hint="Valor pago uma única vez. Na locação, normalmente é a implantação/adesão. Deixe 0 se não houver.">
          <MoneyInput value={m.valorUnico} onChange={(v) => set("valorUnico", v)} />
        </Field>
        <Field label="Parcelas" hint="Em quantas vezes o valor único pode ser dividido. 1 = à vista.">
          <Input type="number" min="1" max="48" value={m.parcelas} onChange={(e) => set("parcelas", Math.max(1, Number(e.target.value) || 1))} className="h-9 text-right tabular-nums" />
        </Field>
        <Field label="Mensalidade" hint="Valor cobrado todo mês (licença, suporte, hospedagem). Deixe 0 na compra sem manutenção.">
          <MoneyInput value={m.valorMensal} onChange={(v) => set("valorMensal", v)} />
        </Field>
        <Field label="Fidelidade" hint="Prazo mínimo de contrato em meses. 0 = sem fidelidade.">
          <div className="relative">
            <Input type="number" min="0" max="60" value={m.fidelidadeMeses} onChange={(e) => set("fidelidadeMeses", Math.max(0, Number(e.target.value) || 0))} className="h-9 pr-14 text-right tabular-nums" />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-muted-foreground">meses</span>
          </div>
        </Field>
        <Field label="Descrição curta" className="sm:col-span-2">
          <Textarea value={m.descricao ?? ""} onChange={(e) => set("descricao", e.target.value)} rows={3} placeholder="Ex.: Sem investimento alto na entrada. Atualizações inclusas." className="resize-none text-[13px]" />
        </Field>
        <Field label="O que está incluso" hint="Um item por linha. Aparece como lista com check para o cliente." className="sm:col-span-2">
          <Textarea
            value={m.inclusos.join("\n")}
            onChange={(e) => set("inclusos", e.target.value.split("\n"))}
            onBlur={() => set("inclusos", m.inclusos.map((s) => s.trim()).filter(Boolean))}
            rows={3}
            placeholder={"Suporte técnico\nAtualizações\nBackup em nuvem"}
            className="resize-none text-[13px]"
          />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-border bg-[hsl(228_8%_7%)] px-4 py-2.5 text-[12px] text-muted-foreground">
        {m.valorUnico > 0 && (
          <span>
            Entrada <strong className="font-semibold tabular-nums text-foreground">{m.parcelas > 1 ? `${m.parcelas}x ${fmtCurrency(m.valorUnico / m.parcelas)}` : fmtCurrency(m.valorUnico)}</strong>
          </span>
        )}
        {m.valorMensal > 0 && (
          <span>
            Mensal <strong className="font-semibold tabular-nums text-foreground">{fmtCurrency(m.valorMensal)}</strong>
          </span>
        )}
        <span className="ml-auto">
          Total em {meses} meses <strong className="font-semibold tabular-nums text-foreground">{fmtCurrency(custoNoPeriodo(m, meses))}</strong>
        </span>
      </div>
    </div>
  )
}

// ── Preview lateral ───────────────────────────────────────────────────────────

function ResumoLateral({ form }: { form: Proposta }) {
  const totais = totaisDosItens(form.itens)
  const modalidades = form.modalidades ?? []
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">O cliente verá</div>
        <div className="mt-2 truncate text-[14px] font-semibold text-foreground">{form.titulo || "Proposta sem título"}</div>
        <div className="truncate text-[12px] text-muted-foreground">{form.clienteEmpresa || form.clienteNome || "Cliente não informado"}</div>
      </div>

      {modalidades.length > 0 ? (
        <div className="space-y-2">
          {modalidades.map((m) => {
            const meta = TIPO_META[m.tipo]
            return (
              <div key={m.id} className={cn("rounded-lg border p-3", m.recomendada ? "border-[hsl(224_100%_64%/0.55)] bg-[hsl(224_100%_64%/0.06)]" : "border-border bg-[hsl(228_8%_9%)]")}>
                <div className="flex items-center gap-2 text-[12px]">
                  <meta.icon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate font-medium text-foreground">{m.nome || meta.label}</span>
                  {m.recomendada && <Star className="ml-auto h-3 w-3 fill-[hsl(224_100%_75%)] text-[hsl(224_100%_75%)]" />}
                </div>
                <div className="mt-2 space-y-0.5 tabular-nums">
                  {m.valorUnico > 0 && (
                    <div className="text-[15px] font-semibold text-foreground">
                      {m.parcelas > 1 ? `${m.parcelas}x ${fmtCurrency(m.valorUnico / m.parcelas)}` : fmtCurrency(m.valorUnico)}
                      <span className="ml-1 text-[11px] font-normal text-muted-foreground">{m.tipo === "locacao" ? "adesão" : "único"}</span>
                    </div>
                  )}
                  {m.valorMensal > 0 && (
                    <div className={cn("font-semibold text-[hsl(258_100%_82%)]", m.valorUnico > 0 ? "text-[13px]" : "text-[15px]")}>
                      {m.valorUnico > 0 && "+ "}
                      {fmtCurrency(m.valorMensal)}
                      <span className="text-[11px] font-normal text-muted-foreground">/mês</span>
                    </div>
                  )}
                  {m.valorUnico === 0 && m.valorMensal === 0 && <div className="text-[13px] text-muted-foreground">Defina os valores</div>}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-[hsl(228_8%_9%)] p-3 tabular-nums">
          <div className="text-[11px] text-muted-foreground">Investimento</div>
          <div className="mt-1 text-[18px] font-semibold text-foreground">{fmtCurrency(form.valorFinal)}</div>
          {totais.mensal > 0 && <div className="text-[13px] font-medium text-[hsl(258_100%_82%)]">+ {fmtCurrency(totais.mensal)}/mês</div>}
          <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">Sem modalidades, o cliente vê só o total dos itens.</p>
        </div>
      )}

      <div className="space-y-1.5 border-t border-border pt-3 text-[12px]">
        <div className="flex justify-between text-muted-foreground"><span>Itens únicos</span><span className="tabular-nums text-foreground">{fmtCurrency(totais.unica)}</span></div>
        <div className="flex justify-between text-muted-foreground"><span>Itens mensais</span><span className="tabular-nums text-foreground">{fmtCurrency(totais.mensal)}/mês</span></div>
        <div className="flex justify-between text-muted-foreground"><span>Validade</span><span className="tabular-nums text-foreground">{form.validadeAte ? new Date(`${form.validadeAte}T00:00:00`).toLocaleDateString("pt-BR") : "—"}</span></div>
        <div className="flex justify-between text-muted-foreground"><span>Protegida por senha</span><span className="text-foreground">{form.senha ? "Sim" : "Não"}</span></div>
      </div>
    </div>
  )
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface PropostaEditorProps {
  open: boolean
  proposta: Proposta | null
  onClose: () => void
  onSave: (proposta: Proposta) => void
}

// ── Component ─────────────────────────────────────────────────────────────────

const PropostaEditor = ({ open, proposta, onClose, onSave }: PropostaEditorProps) => {
  const { toast } = useToast()
  const [form, setForm] = useState<Proposta>(emptyProposta())
  const [secao, setSecao] = useState<Secao>("cliente")
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      setForm(proposta ? { ...proposta, modalidades: proposta.modalidades ?? [] } : emptyProposta())
      setSecao("cliente")
    }
  }, [open, proposta])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [secao])

  // Total da proposta = itens de cobrança única - desconto (mensalidades à parte)
  useEffect(() => {
    const total = form.itens.reduce((s, i) => s + i.valorTotal, 0)
    const final = Math.max(0, total - form.desconto)
    setForm((prev) => (prev.valorTotal === total && prev.valorFinal === final ? prev : { ...prev, valorTotal: total, valorFinal: final }))
  }, [form.itens, form.desconto])

  const set = <K extends keyof Proposta>(field: K, value: Proposta[K]) => setForm((prev) => ({ ...prev, [field]: value }))

  // ── Itens ──
  const addItem = () => {
    const item: ItemProposta = { id: uid(), descricao: "", quantidade: 1, valorUnitario: 0, valorTotal: 0, cobranca: "unica" }
    setForm((prev) => ({ ...prev, itens: [...prev.itens, item] }))
  }

  const updateItem = <K extends keyof ItemProposta>(idx: number, field: K, value: ItemProposta[K]) => {
    setForm((prev) => {
      const itens = [...prev.itens]
      const item = { ...itens[idx], [field]: value }
      if (field === "quantidade" || field === "valorUnitario") {
        item.valorTotal = Number(item.quantidade) * Number(item.valorUnitario)
      }
      itens[idx] = item
      return { ...prev, itens }
    })
  }

  const removeItem = (idx: number) => setForm((prev) => ({ ...prev, itens: prev.itens.filter((_, i) => i !== idx) }))

  // ── Modalidades ──
  const modalidades = form.modalidades ?? []
  const setModalidades = (next: ModalidadeProposta[]) => set("modalidades", next)
  const addModalidade = (tipo: TipoModalidade) => setModalidades([...modalidades, novaModalidade(tipo, form.itens)])
  const addCompraELocacao = () => {
    const compra = novaModalidade("compra", form.itens)
    const locacao = { ...novaModalidade("locacao", form.itens), recomendada: true }
    setModalidades([...modalidades, compra, locacao])
  }

  const totais = useMemo(() => totaisDosItens(form.itens), [form.itens])

  // ── Validação ──
  const validate = () => {
    const faltando = !form.titulo.trim() ? "o título" : !form.clienteNome.trim() ? "o nome do cliente" : null
    if (faltando) {
      setSecao("cliente")
      toast({ title: "Falta preencher", description: `Informe ${faltando} para salvar.`, variant: "destructive" })
      return false
    }
    const semValor = modalidades.find((m) => m.valorUnico <= 0 && m.valorMensal <= 0)
    if (semValor) {
      setSecao("modalidades")
      toast({ title: "Modalidade sem valor", description: `"${semValor.nome}" precisa de valor único ou mensalidade.`, variant: "destructive" })
      return false
    }
    return true
  }

  const normalizado = (): Proposta => ({
    ...form,
    modalidades: modalidades.map((m) => ({ ...m, inclusos: m.inclusos.map((s) => s.trim()).filter(Boolean) })),
    updatedAt: new Date().toISOString(),
  })

  const handleSaveDraft = () => {
    if (!validate()) return
    onSave(normalizado())
  }

  const handleSend = () => {
    if (!validate()) return
    const evento = { id: uid(), tipo: "enviada" as const, data: new Date().toISOString(), descricao: "Proposta marcada como enviada" }
    onSave({ ...normalizado(), status: "enviada", historico: [...(form.historico ?? []), evento] })
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicUrl(form)).then(() => {
      toast({ title: "Link copiado", description: "Envie para o cliente aceitar a proposta." })
    })
  }

  const completo: Record<Secao, boolean> = {
    cliente: !!form.titulo.trim() && !!form.clienteNome.trim(),
    itens: form.itens.length > 0,
    modalidades: modalidades.length > 0,
    conteudo: !!(form.escopo || "").trim(),
    compartilhar: form.status !== "rascunho",
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex h-[min(92vh,860px)] w-[min(96vw,1240px)] max-w-none flex-col gap-0 overflow-hidden border-border bg-[hsl(228_11%_6.5%)] p-0 sm:rounded-2xl [&>button.absolute]:hidden">
        {/* Header */}
        <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-5">
          <DialogTitle className="truncate text-[15px] font-semibold">{proposta ? "Editar proposta" : "Nova proposta"}</DialogTitle>
          <StatusPill status={form.status} />
          {form.titulo && <span className="hidden truncate text-[13px] text-muted-foreground md:inline">· {form.titulo}</span>}
          <Button variant="ghost" size="icon" className="ml-auto h-8 w-8 text-muted-foreground" onClick={onClose} aria-label="Fechar">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex min-h-0 flex-1">
          {/* Steps */}
          <nav className="hidden w-56 shrink-0 border-r border-border p-3 md:block" aria-label="Seções da proposta">
            <ol className="space-y-0.5">
              {SECOES.map((s, i) => {
                const active = secao === s.id
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => setSecao(s.id)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-[hsl(228_8%_11%)]",
                        active && "bg-[hsl(228_8%_12.5%)]",
                      )}
                    >
                      <span
                        className={cn(
                          "grid h-6 w-6 shrink-0 place-items-center rounded-full border text-[11px] font-semibold tabular-nums",
                          completo[s.id]
                            ? "border-[hsl(152_60%_52%/0.4)] bg-[hsl(152_60%_52%/0.12)] text-[hsl(152_60%_62%)]"
                            : active
                              ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]"
                              : "border-border text-muted-foreground",
                        )}
                      >
                        {completo[s.id] ? <Check className="h-3 w-3" strokeWidth={3} /> : i + 1}
                      </span>
                      <span className="min-w-0">
                        <span className={cn("block text-[13px] font-medium", active ? "text-foreground" : "text-[hsl(225_7%_70%)]")}>{s.label}</span>
                        <span className="block truncate text-[11px] text-muted-foreground">{s.hint}</span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ol>
          </nav>

          {/* Content */}
          <div ref={scrollRef} className="superadmin-scrollbar min-w-0 flex-1 overflow-y-auto">
            {/* Mobile step tabs */}
            <div className="flex gap-1 overflow-x-auto border-b border-border px-3 py-2 md:hidden">
              {SECOES.map((s) => (
                <button key={s.id} type="button" onClick={() => setSecao(s.id)} className={cn("shrink-0 rounded-md px-3 py-1.5 text-[12px] font-medium", secao === s.id ? "bg-[hsl(228_8%_14%)] text-foreground" : "text-muted-foreground")}>
                  {s.label}
                </button>
              ))}
            </div>

            <div className="mx-auto max-w-3xl px-5 py-6 sm:px-8">
              {secao === "cliente" && (
                <>
                  <SectionHeader title="Cliente e proposta" description="Identificação que aparece no topo da página do cliente." />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Título da proposta *" htmlFor="titulo" className="sm:col-span-2">
                      <Input id="titulo" value={form.titulo} onChange={(e) => set("titulo", e.target.value)} placeholder="Ex.: Sistema de gestão — IG Motos" className="h-9" />
                    </Field>
                    <Field label="Nome do cliente *" htmlFor="clienteNome">
                      <Input id="clienteNome" value={form.clienteNome} onChange={(e) => set("clienteNome", e.target.value)} placeholder="Nome completo" className="h-9" />
                    </Field>
                    <Field label="Empresa / condomínio" htmlFor="clienteEmpresa">
                      <Input id="clienteEmpresa" value={form.clienteEmpresa ?? ""} onChange={(e) => set("clienteEmpresa", e.target.value)} placeholder="Razão social ou nome do condomínio" className="h-9" />
                    </Field>
                    <Field label="E-mail" htmlFor="clienteEmail">
                      <Input id="clienteEmail" type="email" value={form.clienteEmail} onChange={(e) => set("clienteEmail", e.target.value)} placeholder="email@exemplo.com" className="h-9" />
                    </Field>
                    <Field label="Telefone / WhatsApp" htmlFor="clienteTelefone">
                      <Input id="clienteTelefone" value={form.clienteTelefone ?? ""} onChange={(e) => set("clienteTelefone", e.target.value)} placeholder="(21) 99999-9999" className="h-9" />
                    </Field>
                    <Field label="Válida até *" htmlFor="validadeAte" hint="Depois desta data a proposta aparece como vencida e os valores podem ser revistos.">
                      <Input id="validadeAte" type="date" value={form.validadeAte} onChange={(e) => set("validadeAte", e.target.value)} className="h-9 [color-scheme:dark]" />
                    </Field>
                  </div>
                </>
              )}

              {secao === "itens" && (
                <>
                  <SectionHeader
                    title="Itens da proposta"
                    description="Liste módulos, equipamentos e serviços. Marque o que é cobrado todo mês."
                    action={
                      <Button size="sm" variant="outline" onClick={addItem} className="h-8 gap-1.5">
                        <Plus className="h-4 w-4" />Adicionar item
                      </Button>
                    }
                  />
                  {form.itens.length === 0 ? (
                    <button type="button" onClick={addItem} className="flex w-full flex-col items-center gap-2 rounded-xl border border-dashed border-border py-12 text-[13px] text-muted-foreground transition-colors hover:border-[hsl(228_8%_24%)] hover:text-foreground">
                      <Layers className="h-6 w-6" />
                      Nenhum item ainda. Clique para adicionar o primeiro.
                    </button>
                  ) : (
                    <div className="overflow-hidden rounded-xl border border-border">
                      <div className="hidden grid-cols-[1fr_128px_72px_120px_104px_36px] gap-2 border-b border-border bg-[hsl(228_8%_8%)] px-3 py-2 text-[11px] font-medium uppercase tracking-[0.04em] text-muted-foreground sm:grid">
                        <span>Descrição</span><span>Cobrança</span><span className="text-right">Qtd</span><span className="text-right">Valor unit.</span><span className="text-right">Total</span><span />
                      </div>
                      <div className="divide-y divide-border">
                        {form.itens.map((item, idx) => {
                          const cobranca = cobrancaDoItem(item)
                          return (
                            <div key={item.id} className="grid grid-cols-2 items-center gap-2 px-3 py-2.5 sm:grid-cols-[1fr_128px_72px_120px_104px_36px]">
                              <Input value={item.descricao} onChange={(e) => updateItem(idx, "descricao", e.target.value)} placeholder="Descrição do item" aria-label="Descrição" className="col-span-2 h-9 sm:col-span-1" />
                              <CobrancaToggle value={cobranca} onChange={(v) => updateItem(idx, "cobranca", v)} />
                              <Input type="number" min="1" step="1" value={item.quantidade} onChange={(e) => updateItem(idx, "quantidade", Number(e.target.value))} aria-label="Quantidade" className="h-9 text-right tabular-nums" />
                              <MoneyInput value={item.valorUnitario} onChange={(v) => updateItem(idx, "valorUnitario", v)} />
                              <div className="text-right text-[13px] font-medium tabular-nums text-foreground">
                                {fmtCurrency(item.valorTotal)}
                                {cobranca === "mensal" && <span className="text-[11px] font-normal text-muted-foreground">/mês</span>}
                              </div>
                              <Button size="icon" variant="ghost" className="h-8 w-8 justify-self-end text-muted-foreground hover:text-[hsl(var(--destructive))]" onClick={() => removeItem(idx)} aria-label="Remover item">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )
                        })}
                      </div>
                      <div className="space-y-2 border-t border-border bg-[hsl(228_8%_7.5%)] px-4 py-3 text-[13px]">
                        <div className="flex justify-between text-muted-foreground"><span>Subtotal único</span><span className="tabular-nums text-foreground">{fmtCurrency(totais.unica)}</span></div>
                        <div className="flex items-center justify-between text-muted-foreground">
                          <span className="flex items-center gap-1.5">
                            Desconto
                            <Tooltip><TooltipTrigger asChild><HelpCircle className="h-3.5 w-3.5 cursor-help" /></TooltipTrigger><TooltipContent className="max-w-60 text-xs">Abatido do total dos itens. Para modalidades, ajuste os valores direto em cada opção.</TooltipContent></Tooltip>
                          </span>
                          <MoneyInput value={form.desconto} onChange={(v) => set("desconto", v)} className="w-36" />
                        </div>
                        <div className="flex justify-between text-muted-foreground"><span>Mensalidades</span><span className="tabular-nums text-[hsl(258_100%_82%)]">{fmtCurrency(totais.mensal)}/mês</span></div>
                        <div className="flex justify-between border-t border-border pt-2 text-[14px] font-semibold"><span>Total</span><span className="tabular-nums text-foreground">{fmtCurrency(form.valorFinal)}</span></div>
                      </div>
                    </div>
                  )}
                  {form.itens.length > 0 && modalidades.length === 0 && (
                    <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[hsl(224_100%_64%/0.3)] bg-[hsl(224_100%_64%/0.06)] p-4 sm:flex-row sm:items-center">
                      <ShoppingBag className="h-5 w-5 shrink-0 text-[hsl(224_100%_76%)]" />
                      <p className="flex-1 text-[13px] text-[hsl(225_10%_82%)]">O cliente quer opção de <strong>comprar</strong> ou <strong>alugar</strong>? Ofereça as duas modalidades lado a lado.</p>
                      <Button size="sm" className="h-8" onClick={() => setSecao("modalidades")}>Configurar modalidades</Button>
                    </div>
                  )}
                </>
              )}

              {secao === "modalidades" && (
                <>
                  <SectionHeader
                    title="Modalidades de contratação"
                    description="Opções que o cliente compara e escolhe ao aceitar — por exemplo, comprar o sistema ou alugar por mensalidade."
                    action={
                      modalidades.length > 0 && (
                        <div className="flex gap-1.5">
                          {(Object.keys(TIPO_META) as TipoModalidade[]).map((t) => {
                            const meta = TIPO_META[t]
                            return (
                              <Tooltip key={t}>
                                <TooltipTrigger asChild>
                                  <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => addModalidade(t)}>
                                    <Plus className="h-3.5 w-3.5" />{meta.label}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Adicionar opção de {meta.label.toLowerCase()}</TooltipContent>
                              </Tooltip>
                            )
                          })}
                        </div>
                      )
                    }
                  />

                  {modalidades.length === 0 ? (
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={addCompraELocacao}
                        className="group flex w-full items-center gap-4 rounded-xl border border-[hsl(224_100%_64%/0.45)] bg-[hsl(224_100%_64%/0.07)] p-5 text-left transition-colors hover:bg-[hsl(224_100%_64%/0.11)]"
                      >
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[hsl(224_100%_64%)] text-white"><Sparkles className="h-5 w-5" /></span>
                        <span className="flex-1">
                          <span className="block text-[14px] font-semibold text-foreground">Compra + Locação mensal</span>
                          <span className="block text-[13px] text-muted-foreground">Cria as duas opções já preenchidas com o total dos itens. Você só ajusta os valores.</span>
                        </span>
                        <span className="text-[12px] font-medium text-[hsl(224_100%_78%)]">Recomendado</span>
                      </button>
                      <div className="grid gap-3 sm:grid-cols-3">
                        {(Object.keys(TIPO_META) as TipoModalidade[]).map((t) => {
                          const meta = TIPO_META[t]
                          return (
                            <button key={t} type="button" onClick={() => addModalidade(t)} className="flex items-center gap-3 rounded-xl border border-border p-4 text-left transition-colors hover:border-[hsl(228_8%_24%)] hover:bg-[hsl(228_8%_9%)]">
                              <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-lg", meta.tone)}><meta.icon className="h-4 w-4" /></span>
                              <span>
                                <span className="block text-[13px] font-medium text-foreground">Só {meta.label.toLowerCase()}</span>
                                <span className="block text-[11px] text-muted-foreground">Uma opção</span>
                              </span>
                            </button>
                          )
                        })}
                      </div>
                      <p className="pt-2 text-[12px] leading-relaxed text-muted-foreground">
                        Sem modalidades, a proposta mostra apenas o total dos itens ({fmtCurrency(form.valorFinal)}{totais.mensal > 0 ? ` + ${fmtCurrency(totais.mensal)}/mês` : ""}).
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {modalidades.map((m, i) => (
                        <ModalidadeEditor
                          key={m.id}
                          m={m}
                          index={i}
                          onChange={(next) => setModalidades(modalidades.map((x) => (x.id === m.id ? next : x)))}
                          onRemove={() => setModalidades(modalidades.filter((x) => x.id !== m.id))}
                          onRecomendar={() => setModalidades(modalidades.map((x) => ({ ...x, recomendada: x.id === m.id ? !m.recomendada : false })))}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {secao === "conteudo" && (
                <>
                  <SectionHeader title="Conteúdo" description="Textos exibidos na página da proposta." />
                  <div className="space-y-4">
                    <Field label="Escopo do projeto" htmlFor="escopo" hint="A primeira frase vira o subtítulo no topo da página do cliente.">
                      <Textarea id="escopo" value={form.escopo ?? ""} onChange={(e) => set("escopo", e.target.value)} placeholder="Descreva o que está incluso nesta proposta…" className="min-h-[120px] text-[13px]" />
                    </Field>
                    <Field label="Pagamento e observações" htmlFor="observacoes">
                      <Textarea id="observacoes" value={form.observacoes ?? ""} onChange={(e) => set("observacoes", e.target.value)} placeholder="Formas de pagamento, prazos de entrega…" className="min-h-[96px] text-[13px]" />
                    </Field>
                    <Field label="Termos e condições" htmlFor="termos">
                      <Textarea id="termos" value={form.termos ?? ""} onChange={(e) => set("termos", e.target.value)} className="min-h-[96px] text-[13px]" />
                    </Field>
                  </div>
                </>
              )}

              {secao === "compartilhar" && (
                <>
                  <SectionHeader title="Compartilhar" description="O cliente abre o link, compara as opções e aceita sem precisar de login." />
                  <div className="space-y-4">
                    <div className="rounded-xl border border-border bg-[hsl(228_8%_8%)] p-4">
                      <div className="text-[12px] font-medium text-[hsl(225_8%_74%)]">Link público</div>
                      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                        <code className="flex h-9 min-w-0 flex-1 items-center truncate rounded-md border border-input bg-background px-3 font-mono text-[12px] text-muted-foreground">{publicUrl(form)}</code>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={handleCopyLink} className="h-9 gap-1.5"><Copy className="h-3.5 w-3.5" />Copiar</Button>
                          <Button size="sm" variant="outline" asChild className="h-9 w-9 p-0">
                            <a href={publicUrl(form)} target="_blank" rel="noopener noreferrer" aria-label="Abrir link"><ExternalLink className="h-3.5 w-3.5" /></a>
                          </Button>
                        </div>
                      </div>
                      {!proposta && <p className="mt-2 text-[11px] text-muted-foreground">O link só funciona depois que a proposta for salva.</p>}
                    </div>
                    <Field label="Senha de visualização (opcional)" htmlFor="senha" hint="Protege dados sensíveis (LGPD). O cliente precisa digitar a senha para ver a proposta.">
                      <div className="relative">
                        <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input id="senha" value={form.senha ?? ""} onChange={(e) => set("senha", e.target.value)} placeholder="Sem senha" className="h-9 pl-9" />
                      </div>
                    </Field>
                    <ul className="space-y-1.5 text-[12px] text-muted-foreground">
                      <li className="flex gap-2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(152_60%_52%)]" />O cliente escolhe a modalidade e aceita eletronicamente.</li>
                      <li className="flex gap-2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(152_60%_52%)]" />Pode também pedir revisão ou recusar com justificativa.</li>
                      <li className="flex gap-2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(152_60%_52%)]" />Cada decisão fica registrada no histórico da proposta.</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Live summary */}
          <aside className="superadmin-scrollbar hidden w-72 shrink-0 overflow-y-auto border-l border-border bg-[hsl(228_10%_7%)] p-5 lg:block">
            <ResumoLateral form={{ ...form, modalidades }} />
          </aside>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center gap-2 border-t border-border px-5 py-3">
          <Button variant="ghost" onClick={onClose} className="h-9 text-muted-foreground">Cancelar</Button>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={handleSaveDraft} className="h-9 gap-1.5"><Save className="h-4 w-4" />Salvar{form.status === "rascunho" ? " rascunho" : ""}</Button>
            {form.status === "rascunho" && (
              <Button onClick={handleSend} className="h-9 gap-1.5"><Send className="h-4 w-4" />Salvar e marcar enviada</Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PropostaEditor
